import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdvanceTable from './AdvanceTable';
import OffersTable from './OffersTable';
import AdvancePaymentsTable from './AdvancePaymentsTable';

interface BusinessProps {
  email: string;
  loggedIn: boolean;
  token: string;
  setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  baseUrl: string;
}

const Business: React.FC<BusinessProps> = ({ email, loggedIn, token, setLoggedIn, setEmail, baseUrl }) => {
  const [advanceData, setAdvanceData] = useState<{ data: Array<{ 
    id: string, 
    business_id: string, 
    offer_id: string, 
    payout_bank_account_id: string,
    terms: { [key: string]: any },
    payments_start_at: string,
    outstanding_balance: { [key: string]: any },
    execution_status: string,
    created_at: string,
    executed_at: string,
    missing_agreements: { [key: string]: any }
  }> }>({ 
    data: [] 
  });

  const [businessData, setBusinessData] = useState({
    id: "",
    external_business_id: "",
    legal_name: "",
    tin_mask: "",
    website: "",
    head_office_address: {
      line1: "",
      city: "",
      state: "",
      postal_code: "",
      country: ""
    },
    industry_code: {
      value: "",
      format: ""
    },
    compliance_status: ""
  });


  const [offersData, setOffersData] = useState<{ data: Array<{ 
    id: string, 
    business_id: string, 
    created_at: string, 
    expires_at: string, 
    target_payback_period_days: number | string, 
    maximum_amount: number, 
    originated_amount: number, 
    available_amount: number, 
    currency: string, 
    status: string, 
    boosted: boolean | string 
  }> }>({
    data: [
      {
        id: "",
        business_id: "",
        created_at: "",
        expires_at: "",
        target_payback_period_days: "",
        maximum_amount: 0,
        originated_amount: 0,
        available_amount: 0,
        currency: "",
        status: "",
        boosted: ""
      }
    ]
  });

  const [advanceId, setAdvanceId] = useState<string>('');

  const [advancePaymentsData, setAdvancePaymentsData] = useState<{ data: Array<{ 
    id  : string, 
    business_id: string, 
    advance_id: string, 
    amount: number,
    currency: string,
    effective_at: string,
    due_at: string,
    status: string
  }> }>({
    data: []
  });


  const [loading, setLoading] = useState<boolean>(true); // Loading state

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get('business_id'); // Retrieve business_id from query parameter

  useEffect(() => {
    const fetchData = async (  endPoint: string, // Defining endPoint as a string
    queryParam: string, // Defining queryParam as a string
    queryParamInput: string // Defining queryParamInput as a string or number
  ) => {
      setLoading(true); // Set loading to true before fetching
      try {
        const response = await fetch(`${baseUrl}${endPoint}?${queryParam}=${queryParamInput}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'jwt-token': token,
          },
        });

        const data = await response.json();
        console.log(data);

        if (data.data || data.external_business_id) {
          if (endPoint === "advances") {
            setAdvanceData(data); // Update advance data state
          }
          if (endPoint === "business") {
            setBusinessData(data); // Update business data state
          }
          if (endPoint === "offers") {
            setOffersData(data); // Update business data state
          }
          if (endPoint === "advancePayments") {
            setAdvancePaymentsData(data); // Update business data state
          }

        } else {
          console.error("Error: No items found");
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    if (businessId) {
      fetchData("advances","business_id",businessId); // Call the fetchData function only if businessId exists
      fetchData("business","business_id",businessId); // Call the fetchData function only if businessId exists
      fetchData("offers","business_id",businessId); // Call the fetchData function only if businessId exists
      fetchData("advancePayments","business_id",businessId); // Call the fetchData function only if businessId exists

    }
    if(advanceId) {
    }
  }, [businessId, token, baseUrl, advanceId]); // Dependency array: re-fetch if businessId, token, or baseUrl changes

  return (
    <div>
      <div className={'titleContainer'}>
        <div>Business Overview</div>
      </div>

      <div style={{ display: 'flex', gap: '20px' }}>
      <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', maxWidth: '400px', fontFamily: 'Arial, sans-serif' }}>
      <h2>Business Details</h2>
  <p><strong>ID:</strong> {businessData?.id || "N/A"}</p>
  <p><strong>External Business ID:</strong> {businessData?.external_business_id || "N/A"}</p>
  <p><strong>Legal Name:</strong> {businessData?.legal_name || "N/A"}</p>
  <p><strong>TIN Mask:</strong> {businessData?.tin_mask || "N/A"}</p>
  <p><strong>Website:</strong> 
    <a href={`https://${businessData?.website}`} target="_blank" rel="noopener noreferrer">
      {businessData?.website || "N/A"}
    </a>
  </p>

  <div style={{ marginTop: '12px' }}>
    <h3 style={{ color: '#333', marginBottom: '8px' }}>Head Office Address</h3>
    <p><strong>Line 1:</strong> {businessData?.head_office_address?.line1 || "N/A"}</p>
    <p><strong>City:</strong> {businessData?.head_office_address?.city || "N/A"}</p>
    <p><strong>State:</strong> {businessData?.head_office_address?.state || "N/A"}</p>
    <p><strong>Postal Code:</strong> {businessData?.head_office_address?.postal_code || "N/A"}</p>
    <p><strong>Country:</strong> {businessData?.head_office_address?.country || "N/A"}</p>
  </div>

  <div style={{ marginTop: '12px' }}>
    <h3 style={{ color: '#333', marginBottom: '8px' }}>Industry Code</h3>
    <p><strong>Value:</strong> {businessData?.industry_code?.value || "N/A"}</p>
    <p><strong>Format:</strong> {businessData?.industry_code?.format || "N/A"}</p>
  </div>

  <p style={{ marginTop: '12px' }}><strong>Compliance Status:</strong> {businessData?.compliance_status || "N/A"}</p>
</div>
<div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', maxWidth: '400px', fontFamily: 'Arial, sans-serif', margin: '20px' }}>
    <h2>Latest offer</h2>

<div style={{ marginTop: '12px' }}>
  <p><strong>Status:</strong> {offersData?.data[0]?.status || "N/A"}</p>
</div>

<div style={{ marginTop: '20px' }}>
  <h3 style={{ color: '#333', marginBottom: '8px' }}>Amounts</h3>
  <p><strong>Maximum:</strong> {offersData?.data[0]?.currency.toUpperCase() || "N/A"} {offersData?.data[0]?.maximum_amount ? (offersData.data[0].maximum_amount / 100).toFixed(2) : "N/A"} </p>
  <p><strong>Originated:</strong> {offersData?.data[0]?.currency.toUpperCase() || "N/A"} {offersData?.data[0]?.originated_amount ? (offersData.data[0].originated_amount / 100).toFixed(2) : "N/A"} </p>
<p><strong>Available:</strong> {offersData?.data[0]?.currency.toUpperCase() || "N/A"} {offersData?.data[0]?.available_amount ? (offersData.data[0].available_amount / 100).toFixed(2) : "N/A"} </p>
</div>

<div style={{ marginTop: '20px' }}>
  <p><strong>Target Payback Period (Days):</strong> {offersData?.data[0]?.target_payback_period_days || "N/A"}</p>
</div>




<div style={{ marginTop: '12px' }}>
  <p><strong>Boosted:</strong> {offersData?.data[0]?.boosted || "N/A"}</p>
</div>

  </div>
</div>
<br />
      <h2>Offers:</h2>
      <div><OffersTable offersData={offersData}/></div>
<br />
      <h2>Advances:</h2>
      <div><AdvanceTable advanceData={advanceData} setAdvanceId={setAdvanceId}/></div>

      <h2>Advance Payments:</h2>

    <div><AdvancePaymentsTable advancePaymentsData={advancePaymentsData} /></div>

    </div>
  );
};

export default Business;
